import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Shield,
  Users,
  Clock,
  Award,
  Baby,
  MapPin,
  Car,
  ArrowRight,
  Star,
  CheckCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";

const About = () => {
  return (
    <div className="min-h-screen bg-paris-stone overflow-x-hidden" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-midnight-navy pt-24 pb-12 md:pt-28 md:pb-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4 md:mb-6">
              About <span className="text-champagne-gold">Best Disney Transfer</span>
            </h1>
            <p className="text-base md:text-lg text-paris-stone/80 leading-relaxed px-2">
              Your trusted partner for premium private transfers in Paris. 
              We specialize in airport transfers, Disneyland trips, and personalized city tours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-champagne-gold font-semibold mb-3 block text-sm">OUR STORY</span>
              <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-midnight-navy mb-4 md:mb-6">
                Making Every Journey Magical Since Day One
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed text-sm md:text-base">
                <p>
                  Best Disney Transfer was born from a simple idea: families visiting the magic 
                  of Disneyland Paris deserve a stress-free, comfortable journey from the moment 
                  they land in Paris.
                </p>
                <p>
                  Based in Boulogne-Billancourt, we understand that travel with children requires 
                  extra care and attention. That's why we provide free child seats, meet-and-greet 
                  services, and drivers who go above and beyond to make your experience special.
                </p>
                <p>
                  Today, we're proud to serve thousands of families, business travelers, and 
                  tourists each year, maintaining the highest standards of service and reliability.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1629274673673-10d14da3b268?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBibGFjayUyMG1lcmNlZGVzJTIwdi1jbGFzcyUyMHZhbiUyMHRyYW5zZmVyJTIwaW50ZXJpb3IlMjBleHRlcmlvcnxlbnwwfHx8fDE3Njg4MTkzMjJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Our Premium Vehicles"
                className="rounded-2xl shadow-strong w-full"
              />
              <div className="absolute -bottom-4 -left-2 md:-bottom-6 md:-left-6 bg-champagne-gold text-midnight-navy p-4 md:p-6 rounded-xl shadow-lg">
                <p className="font-heading font-bold text-2xl md:text-3xl">1500+</p>
                <p className="text-xs md:text-sm font-medium">Happy Custormers</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 md:py-20 bg-paris-stone">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 md:mb-12"
          >
            <span className="text-champagne-gold font-semibold mb-3 block text-sm">OUR VALUES</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-midnight-navy mb-4">
              What Sets Us Apart
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              {
                icon: Shield,
                title: "Safety First",
                desc: "Licensed, insured, and professionally trained drivers",
              },
              {
                icon: Clock,
                title: "Always On Time",
                desc: "Flight tracking and proactive communication",
              },
              {
                icon: Baby,
                title: "Family Friendly",
                desc: "Free child seats and patient, caring service",
              },
              {
                icon: Award,
                title: "Premium Quality",
                desc: "Modern vehicles, spotless interiors, and comfort",
              },
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-4 md:p-6 shadow-soft text-center"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 bg-midnight-navy rounded-xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <value.icon className="w-6 h-6 md:w-7 md:h-7 text-champagne-gold" />
                </div>
                <h3 className="font-heading font-bold text-base md:text-lg text-midnight-navy mb-1 md:mb-2">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-xs md:text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 md:mb-12"
          >
            <span className="text-champagne-gold font-semibold mb-3 block text-sm">WHY CHOOSE US</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-midnight-navy mb-4">
              The Best Disney Transfer Difference
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-3 md:gap-4 max-w-4xl mx-auto">
            {[
              "Fixed prices with no hidden fees",
              "Free child seats for all ages",
              "Real-time flight tracking",
              "Meet & greet at arrivals",
              "Pay your driver directly",
              "24/7 customer support",
              "Modern, comfortable vehicles",
              "Professional, English-speaking drivers",
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 p-3 md:p-4 bg-paris-stone rounded-xl"
              >
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-midnight-navy font-medium text-sm md:text-base">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 md:py-20 bg-midnight-navy">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-champagne-gold font-semibold mb-3 block text-sm">OUR TEAM</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-white mb-4 md:mb-6">
              Professional Drivers You Can Trust
            </h2>
            <p className="text-paris-stone/80 max-w-2xl mx-auto mb-6 md:mb-8 text-sm md:text-base px-2">
              Our drivers are carefully selected, professionally trained, and share our passion 
              for excellent service. They're not just drivers – they're your first welcome to Paris.
            </p>
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              <div className="text-center">
                <p className="font-heading font-bold text-3xl md:text-4xl text-champagne-gold">10+</p>
                <p className="text-paris-stone/80 text-sm">Professional Drivers</p>
              </div>
              <div className="text-center">
                <p className="font-heading font-bold text-3xl md:text-4xl text-champagne-gold">4.9★</p>
                <p className="text-paris-stone/80 text-sm">Average Rating</p>
              </div>
              <div className="text-center">
                <p className="font-heading font-bold text-3xl md:text-4xl text-champagne-gold">10+</p>
                <p className="text-paris-stone/80 text-sm">Years Experience</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-paris-stone">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-midnight-navy mb-4">
              Ready to Experience the Difference?
            </h2>
            <p className="text-slate-600 mb-6 md:mb-8 max-w-xl mx-auto text-sm md:text-base">
              Book your transfer today and discover why thousands of travelers choose Best Disney Transfer.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
              <Button
                asChild
                size="lg"
                className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-6 md:px-8 rounded-full shadow-lg"
              >
                <Link to="/booking">
                  Book Your Transfer
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-midnight-navy text-midnight-navy hover:bg-midnight-navy hover:text-white font-semibold px-6 md:px-8 rounded-full"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
